import React, { useState, useEffect, useRef } from "react";
import { X, ChevronRight, ChevronLeft, MapPin, Save } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import apiClient from "../lib/api";

interface ProjectOnboardingModalProps {
  open: boolean;
  onComplete: () => void;
  onClose?: () => void;
  initialProject?: ProjectProfile | null; // Optional: for editing existing projects
  projectId?: number | null; // Optional: ID of project being edited (if provided, will update instead of create)
}

type Page = 1 | 2 | 3 | 4 | 5 | 6;

interface ProjectProfile {
  // Page 1
  address: string;
  nickname?: string;
  municipality_id?: string; // Municipality ID from database
  
  // Page 2
  asset_type: string;
  program_attributes: string[];
  program_notes?: string;
  
  // Page 3
  current_stage: string;
  approvals_needed: string[];
  
  // Page 4
  constraint_tags: string[];
  key_risk_note?: string;
  
  // Page 5
  action_style: string;
  notifications: string;
}

interface Municipality {
  id: number;
  name: string;
}

const ASSET_TYPES = [
  "Residential",
  "Mixed-use",
  "Industrial / Logistics",
  "Office",
  "Retail",
  "Institutional / Civic",
  "Land / Subdivision",
  "Other",
];

const PROGRAM_ATTRIBUTES = [
  "Purpose-built rental",
  "Condo",
  "Affordable component",
  "Student housing",
  "Seniors / long-term care",
  "Transit-oriented / station-area",
  "Employment lands adjacency",
  "Heritage context",
  "Brownfield / remediation",
  "Phased development",
  "Other",
];

const CURRENT_STAGES = [
  "Site search / early diligence",
  "Under contract / LOI",
  "Concept development",
  "Pre-application / consultation",
  "Application preparation",
  "Submitted (in circulation)",
  "Under review (iterations)",
  "Public meeting scheduled",
  "Decision pending",
  "Approved (conditions)",
  "In-force / finalized",
  "Permitting / Building permit stage",
  "Construction",
  "On hold",
];

const APPROVALS_NEEDED = [
  "Official Plan Amendment (OPA)",
  "Zoning By-law Amendment (ZBA)",
  "Site Plan Control",
  "Plan of Subdivision",
  "Consent / Severance (Committee of Adjustment)",
  "Minor Variance (Committee of Adjustment)",
  "Part Lot Control",
  "Demolition Permit",
  "Building Permit",
  "Conservation Authority permit/clearance (if applicable)",
  "Other",
];

const CONSTRAINT_TAGS = [
  "Servicing capacity (water/wastewater/storm)",
  "Transportation / access",
  "Development charges / fees sensitivity",
  "Parkland / dedication rate sensitivity",
  "Height / density permissions",
  "Built form / urban design guidelines",
  "Housing policy (e.g., inclusionary zoning / affordability)",
  "Employment lands policy",
  "Heritage constraints",
  "Natural heritage constraints",
  "Floodplain / Conservation Authority constraints",
  "Community opposition / political sensitivity",
  "Appeal risk (OLT)",
  "Other",
];

const ACTION_STYLES = [
  "Monitor only (FYI)",
  "Internal briefing / memo",
  "Schedule meeting (staff/planner)",
  "Submit comments / participate in consultation",
  "Update underwriting / pro forma",
  "Adjust design assumptions",
];

export default function ProjectOnboardingModal({
  open,
  onComplete,
  onClose,
  initialProject,
  projectId,
}: ProjectOnboardingModalProps) {
  const [currentPage, setCurrentPage] = useState<Page>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const [otherAssetType, setOtherAssetType] = useState("");
  const [otherProgramAttribute, setOtherProgramAttribute] = useState("");
  const [otherApproval, setOtherApproval] = useState("");
  const [otherConstraint, setOtherConstraint] = useState("");
  
  // Mapbox autocomplete state
  const addressBoxRef = useRef<HTMLDivElement | null>(null);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrSuggestions, setAddrSuggestions] = useState<any[]>([]);
  const [justSelectedAddress, setJustSelectedAddress] = useState(false);
  
  // Municipalities state
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);
  const [detectedMunicipalityName, setDetectedMunicipalityName] = useState<string | null>(null);
  
  const { toast } = useToast();

  const defaultProfile: ProjectProfile = {
    address: "",
    nickname: "",
    municipality_id: "",
    asset_type: "",
    program_attributes: [],
    program_notes: "",
    current_stage: "",
    approvals_needed: [],
    constraint_tags: [],
    key_risk_note: "",
    action_style: "",
    notifications: "in-app",
  };

  const [profile, setProfile] = useState<ProjectProfile>(initialProject || defaultProfile);

  // Load initial project when modal opens
  useEffect(() => {
    if (open && initialProject) {
      // Extract "Other" values if they exist before setting profile
      let profileToSet = { ...initialProject };
      // Ensure municipality_id is a string if it exists
      if (profileToSet.municipality_id !== undefined && profileToSet.municipality_id !== null) {
        profileToSet.municipality_id = String(profileToSet.municipality_id);
      }
      let otherAsset = "";
      let otherProgram = "";
      let otherApprovalVal = "";
      let otherConstraintVal = "";

      // Check asset type
      if (initialProject.asset_type && !ASSET_TYPES.includes(initialProject.asset_type)) {
        otherAsset = initialProject.asset_type;
        profileToSet.asset_type = "Other";
      }

      // Check program attributes
      if (initialProject.program_attributes) {
        const otherAttr = initialProject.program_attributes.find(a => !PROGRAM_ATTRIBUTES.includes(a));
        if (otherAttr) {
          otherProgram = otherAttr;
          profileToSet.program_attributes = [
            ...initialProject.program_attributes.filter(a => a !== otherAttr),
            "Other"
          ];
        }
      }

      // Check approvals needed
      if (initialProject.approvals_needed) {
        const otherApprovalItem = initialProject.approvals_needed.find(a => !APPROVALS_NEEDED.includes(a));
        if (otherApprovalItem) {
          otherApprovalVal = otherApprovalItem;
          profileToSet.approvals_needed = [
            ...initialProject.approvals_needed.filter(a => a !== otherApprovalItem),
            "Other"
          ];
        }
      }

      // Check constraint tags
      if (initialProject.constraint_tags) {
        const otherConstraintItem = initialProject.constraint_tags.find(c => !CONSTRAINT_TAGS.includes(c));
        if (otherConstraintItem) {
          otherConstraintVal = otherConstraintItem;
          profileToSet.constraint_tags = [
            ...initialProject.constraint_tags.filter(c => c !== otherConstraintItem),
            "Other"
          ];
        }
      }

      setProfile(profileToSet);
      setOtherAssetType(otherAsset);
      setOtherProgramAttribute(otherProgram);
      setOtherApproval(otherApprovalVal);
      setOtherConstraint(otherConstraintVal);
    } else if (open && !initialProject) {
      // Reset to default when opening for new project
      setProfile(defaultProfile);
      setOtherAssetType("");
      setOtherProgramAttribute("");
      setOtherApproval("");
      setOtherConstraint("");
      setCurrentPage(1);
    } else if (open && initialProject && Object.keys(initialProject).length === 1 && initialProject.municipality_id) {
      // Partial initialProject with just municipality_id (for pre-selecting municipality when adding new project)
      setProfile({ ...defaultProfile, municipality_id: initialProject.municipality_id });
      setOtherAssetType("");
      setOtherProgramAttribute("");
      setOtherApproval("");
      setOtherConstraint("");
      setCurrentPage(1);
    }
  }, [open, initialProject]);

  const handleNext = () => {
    if (currentPage < 6) {
      // Validate current page before proceeding
      if (currentPage === 1) {
        if (!profile.address) {
          toast({
            title: "Address required",
            description: "Please enter a project address",
            variant: "destructive",
          });
          return;
        }
        // Check if municipality is selected and valid
        const isMunicipalityValid = profile.municipality_id 
          ? municipalities.some(m => String(m.id) === profile.municipality_id)
          : false;
        if (!profile.municipality_id || !isMunicipalityValid) {
          toast({
            title: "Municipality required",
            description: detectedMunicipalityName 
              ? `Please subscribe to "${detectedMunicipalityName}" municipality or select a different municipality`
              : "Please select a municipality",
            variant: "destructive",
          });
          return;
        }
      }
      if (currentPage === 2 && !profile.asset_type) {
        toast({
          title: "Asset type required",
          description: "Please select an asset type",
          variant: "destructive",
        });
        return;
      }
      if (currentPage === 3 && !profile.current_stage) {
        toast({
          title: "Current stage required",
          description: "Please select a current stage",
          variant: "destructive",
        });
        return;
      }
      if (currentPage === 5 && (!profile.action_style || !profile.notifications)) {
        toast({
          title: "Required fields missing",
          description: "Please select both action style and notification preference",
          variant: "destructive",
        });
        return;
      }
      setCurrentPage((currentPage + 1) as Page);
    }
  };

  const handleBack = () => {
    if (currentPage > 1) {
      setCurrentPage((currentPage - 1) as Page);
    } else if (currentPage === 1 && onClose) {
      // On first page, back button closes the modal
      onClose();
    }
  };

  const prepareProfileData = () => {
    const finalProfile = { ...profile };
    
    if (profile.asset_type === "Other" && otherAssetType) {
      finalProfile.asset_type = otherAssetType;
    }
    
    if (finalProfile.program_attributes.includes("Other") && otherProgramAttribute) {
      finalProfile.program_attributes = [
        ...finalProfile.program_attributes.filter(a => a !== "Other"),
        otherProgramAttribute,
      ];
    }
    
    if (finalProfile.approvals_needed.includes("Other") && otherApproval) {
      finalProfile.approvals_needed = [
        ...finalProfile.approvals_needed.filter(a => a !== "Other"),
        otherApproval,
      ];
    }
    
    if (finalProfile.constraint_tags.includes("Other") && otherConstraint) {
      finalProfile.constraint_tags = [
        ...finalProfile.constraint_tags.filter(c => c !== "Other"),
        otherConstraint,
      ];
    }

    return finalProfile;
  };

  const handleSaveProgress = async () => {
    setIsSavingProgress(true);
    try {
      const finalProfile = prepareProfileData();
      // Convert to project format for API
      const projectData = {
        address: finalProfile.address,
        nickname: finalProfile.nickname || undefined,
        municipality_id: parseInt(finalProfile.municipality_id),
        asset_type: finalProfile.asset_type,
        program_attributes: finalProfile.program_attributes || [],
        program_notes: finalProfile.program_notes || undefined,
        current_stage: finalProfile.current_stage,
        approvals_needed: finalProfile.approvals_needed || [],
        constraint_tags: finalProfile.constraint_tags || [],
        key_risk_note: finalProfile.key_risk_note || undefined,
        action_style: finalProfile.action_style,
        notifications: finalProfile.notifications,
      };
      
      if (projectId) {
        await apiClient.projects.update(projectId, projectData);
      } else {
        await apiClient.projects.create(projectData);
      }
      
      toast({
        title: "Progress saved",
        description: "Your progress has been saved. You can continue later.",
      });
      
      if (onClose) {
        onClose();
      }
    } catch (error: any) {
      toast({
        title: "Error saving progress",
        description: error?.message || "Failed to save progress. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingProgress(false);
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      const finalProfile = prepareProfileData();
      // Convert to project format for API
      const projectData = {
        address: finalProfile.address,
        nickname: finalProfile.nickname || undefined,
        municipality_id: parseInt(finalProfile.municipality_id),
        asset_type: finalProfile.asset_type,
        program_attributes: finalProfile.program_attributes || [],
        program_notes: finalProfile.program_notes || undefined,
        current_stage: finalProfile.current_stage,
        approvals_needed: finalProfile.approvals_needed || [],
        constraint_tags: finalProfile.constraint_tags || [],
        key_risk_note: finalProfile.key_risk_note || undefined,
        action_style: finalProfile.action_style,
        notifications: finalProfile.notifications,
      };
      
      if (projectId) {
        await apiClient.projects.update(projectId, projectData);
      } else {
        await apiClient.projects.create(projectData);
      }
      
      toast({
        title: projectId ? "Project updated" : "Project created",
        description: projectId 
          ? "Your project has been updated successfully"
          : "Your project has been created successfully",
      });
      
      onComplete();
    } catch (error: any) {
      toast({
        title: projectId ? "Error updating project" : "Error creating project",
        description: error?.message || (projectId ? "Failed to update project. Please try again." : "Failed to create project. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateProfile = (updates: Partial<ProjectProfile>) => {
    setProfile((prev) => ({ ...prev, ...updates }));
  };

  const toggleArrayItem = (
    array: string[],
    item: string,
    field: keyof ProjectProfile
  ) => {
    const newArray = array.includes(item)
      ? array.filter((i) => i !== item)
      : [...array, item];
    updateProfile({ [field]: newArray } as Partial<ProjectProfile>);
  };

  // Fetch municipalities on mount
  useEffect(() => {
    const fetchMunicipalities = async () => {
      try {
        setLoadingMunicipalities(true);
        // Use company municipalities instead of properties endpoint
        const data = await apiClient.companyMunicipalities.listAll();
        const municipalitiesData = data.map((cm: any) => ({
          id: cm.municipality_id,
          name: cm.municipality_name
        }));
        setMunicipalities(municipalitiesData || []);
      } catch (err) {
        console.error('Error fetching municipalities:', err);
        setMunicipalities([]);
      } finally {
        setLoadingMunicipalities(false);
      }
    };
    fetchMunicipalities();
  }, []);

  // Close address dropdown on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (addressBoxRef.current && !addressBoxRef.current.contains(e.target as Node)) {
        setAddrOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setAddrOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Try to match municipality from address text (when address changes but no municipality selected)
  useEffect(() => {
    if (profile.address && !profile.municipality_id && municipalities.length > 0 && !justSelectedAddress) {
      const addrLower = profile.address.toLowerCase();
      const match = municipalities.find(m => addrLower.includes(m.name.toLowerCase()));
      if (match) {
        setProfile(prev => ({ ...prev, municipality_id: String(match.id) }));
        setDetectedMunicipalityName(null); // Clear warning if match found
      }
      // If no match found but we previously detected a municipality from Mapbox, keep the warning
      // (don't clear it here - it will only be cleared when user selects a valid municipality or Mapbox detects one)
    } else if (!profile.address) {
      // Address cleared - clear warning
      setDetectedMunicipalityName(null);
    }
  }, [profile.address, profile.municipality_id, municipalities, justSelectedAddress]);
  
  // Validate municipality when it changes - ensure it's in the monitored list
  useEffect(() => {
    if (profile.municipality_id && municipalities.length > 0) {
      const isValid = municipalities.some(m => String(m.id) === profile.municipality_id);
      if (!isValid) {
        // Municipality was manually changed to an invalid one - clear it
        setProfile(prev => ({ ...prev, municipality_id: "" }));
        setDetectedMunicipalityName(null);
      } else {
        // Valid municipality selected - clear any warning
        setDetectedMunicipalityName(null);
      }
    }
  }, [profile.municipality_id, municipalities]);

  // Mapbox autocomplete
  useEffect(() => {
    // Don't search if we just selected an address
    if (justSelectedAddress) {
      setAddrSuggestions([]);
      setAddrOpen(false);
      return;
    }
    
    const token = import.meta.env.VITE_MAPBOX_TOKEN;
    const q = profile.address?.trim();
    
    if (!token || token === 'your_mapbox_token_here' || !q || q.length < 3) {
      setAddrSuggestions([]);
      setAddrOpen(false);
      return;
    }
    
    setAddrLoading(true);
    const h = setTimeout(async () => {
      try {
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?autocomplete=true&types=address,place,locality&country=CA&limit=5&access_token=${token}`;
        const resp = await fetch(url);
        const data = await resp.json();
        setAddrSuggestions(Array.isArray(data?.features) ? data.features : []);
        setAddrOpen(true);
      } catch (err) {
        console.error('Mapbox error:', err);
        setAddrSuggestions([]);
        setAddrOpen(false);
      } finally {
        setAddrLoading(false);
      }
    }, 350);
    return () => clearTimeout(h);
  }, [profile.address, justSelectedAddress]);

  const selectAddress = (feature: any) => {
    const placeName = feature?.place_name || feature?.text || profile.address;
    
    // Set the flag first to prevent immediate re-search
    setJustSelectedAddress(true);
    
    // Update the profile with the selected address
    updateProfile({ address: placeName });
    
    // Extract municipality from context and match to database
    try {
      const context = feature?.context || [];
      const cityObj = context.find((c: any) => (c.id || '').startsWith('place.'))
        || context.find((c: any) => (c.id || '').startsWith('locality.'))
        || context.find((c: any) => (c.id || '').startsWith('district.'));
      const municipalityName = cityObj?.text || '';
      const municipalityNameLower = municipalityName.toLowerCase();
      
      // Try to match municipality by name
      if (municipalityNameLower && municipalities.length > 0) {
        const match = municipalities.find(m => m.name.toLowerCase() === municipalityNameLower);
        if (match) {
          updateProfile({ municipality_id: String(match.id) });
          setDetectedMunicipalityName(null); // Clear warning if match found
        } else {
          // Municipality not in monitored list - show warning
          setDetectedMunicipalityName(municipalityName);
          updateProfile({ municipality_id: "" }); // Clear invalid municipality_id
        }
      } else if (municipalityName) {
        // Municipality name found but no municipalities loaded yet, or no match
        setDetectedMunicipalityName(municipalityName);
        updateProfile({ municipality_id: "" });
      }
    } catch (err) {
      console.error('Error extracting municipality:', err);
    }
    
    // Close the dropdown and clear suggestions
    setAddrOpen(false);
    setAddrSuggestions([]);
  };

  const renderPage1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Project Address</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Enable direct-mention matching (address/street/intersection mentions) and auto-detect municipality.
        </p>
      </div>

      <div className="space-y-4">
        <div ref={addressBoxRef} className="relative">
          <Label htmlFor="address">Address *</Label>
          <Input
            id="address"
            placeholder="Street number + street name + city (postal code optional)"
            value={profile.address}
            onChange={(e) => {
              const newValue = e.target.value;
              updateProfile({ address: newValue });
              
              if (newValue.length === 0 || newValue.length < 3) {
                setJustSelectedAddress(false);
                setAddrOpen(false);
                setAddrSuggestions([]);
                return;
              }
              
              if (justSelectedAddress) {
                return;
              }
            }}
            onFocus={() => {
              if (justSelectedAddress) {
                return;
              }
              
              if (profile.address && profile.address.length >= 3) {
                setAddrOpen(true);
              }
            }}
            onBlur={() => {
              if (justSelectedAddress) {
                return;
              }
              
              setTimeout(() => {
                setAddrOpen(false);
                setAddrSuggestions([]);
              }, 150);
            }}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Start typing to see suggestions.
          </p>
          {addrOpen && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg">
              {addrLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
              ) : addrSuggestions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  {import.meta.env.VITE_MAPBOX_TOKEN === 'your_mapbox_token_here' || !import.meta.env.VITE_MAPBOX_TOKEN 
                    ? "Address autocomplete requires Mapbox token configuration" 
                    : "No suggestions"}
                </div>
              ) : (
                <ul className="max-h-64 overflow-y-auto py-1">
                  {addrSuggestions.map((f: any) => (
                    <li 
                      key={f.id}
                      onClick={() => selectAddress(f)}
                      className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                      {f.place_name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="municipality">Municipality</Label>
          <Select
            value={profile.municipality_id || ""}
            onValueChange={(value) => {
              updateProfile({ municipality_id: value });
              setDetectedMunicipalityName(null); // Clear warning when user manually selects
            }}
          >
            <SelectTrigger id="municipality" disabled={loadingMunicipalities}>
              <SelectValue placeholder={loadingMunicipalities ? "Loading municipalities..." : "Select a municipality"} />
            </SelectTrigger>
            <SelectContent>
              {municipalities.map((municipality) => (
                <SelectItem key={municipality.id} value={String(municipality.id)}>
                  {municipality.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {detectedMunicipalityName && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-md">
              <p className="text-sm text-amber-800 font-medium">
                {detectedMunicipalityName} is not a monitored municipality.
              </p>
              <p className="text-sm text-amber-700 mt-1">
                In order to add this project, you must be monitoring the municipality the project resides in
              </p>
            </div>
          )}
          {!detectedMunicipalityName && (
            <p className="text-xs text-muted-foreground mt-1">
              Municipality is auto-selected from address, but you can change it if needed.
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="nickname">Project nickname (optional)</Label>
          <Input
            id="nickname"
            placeholder="e.g., King & Spadina, Brampton Industrial Campus"
            value={profile.nickname || ""}
            onChange={(e) => updateProfile({ nickname: e.target.value })}
          />
        </div>
      </div>
    </div>
  );

  const renderPage2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Program</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Provide enough context so "Impact on this project" and "Suggested next steps" are not generic.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="asset_type">Asset type *</Label>
          <Select
            value={profile.asset_type}
            onValueChange={(value) => updateProfile({ asset_type: value })}
          >
            <SelectTrigger id="asset_type">
              <SelectValue placeholder="Select asset type" />
            </SelectTrigger>
            <SelectContent>
              {ASSET_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {profile.asset_type === "Other" && (
          <div>
            <Label htmlFor="other_asset_type">Please specify</Label>
            <Input
              id="other_asset_type"
              placeholder="Enter asset type"
              value={otherAssetType}
              onChange={(e) => setOtherAssetType(e.target.value)}
            />
          </div>
        )}

        <div>
          <Label>Program attributes (optional, multi-select)</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Keep compact; these affect interpretation
          </p>
          <div className="border rounded-md p-4 space-y-2 max-h-64 overflow-y-auto">
            {PROGRAM_ATTRIBUTES.map((attr) => (
              <div key={attr} className="flex items-center space-x-2">
                <Checkbox
                  id={`program_attr_${attr}`}
                  checked={profile.program_attributes.includes(attr)}
                  onCheckedChange={() =>
                    toggleArrayItem(profile.program_attributes, attr, "program_attributes")
                  }
                />
                <Label
                  htmlFor={`program_attr_${attr}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {attr}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {profile.program_attributes.includes("Other") && (
          <div>
            <Label htmlFor="other_program_attribute">Please specify</Label>
            <Input
              id="other_program_attribute"
              placeholder="e.g., film studio, data centre, hotel"
              value={otherProgramAttribute}
              onChange={(e) => setOtherProgramAttribute(e.target.value)}
            />
          </div>
        )}

        <div>
          <Label htmlFor="program_notes">Program notes (optional)</Label>
          <Textarea
            id="program_notes"
            placeholder="Anything we should know to interpret impacts? (e.g., Height sensitive; trying to avoid OPA if possible.)"
            value={profile.program_notes || ""}
            onChange={(e) => updateProfile({ program_notes: e.target.value })}
            maxLength={200}
            rows={3}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {(profile.program_notes || "").length}/200 characters
          </p>
        </div>
      </div>
    </div>
  );

  const renderPage3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Stage & Approvals</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Let Cityscrape tailor implications and next steps to where the file is in the process.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="current_stage">Current stage *</Label>
          <Select
            value={profile.current_stage}
            onValueChange={(value) => updateProfile({ current_stage: value })}
          >
            <SelectTrigger id="current_stage">
              <SelectValue placeholder="Select current stage" />
            </SelectTrigger>
            <SelectContent>
              {CURRENT_STAGES.map((stage) => (
                <SelectItem key={stage} value={stage}>
                  {stage}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label>Approvals needed (optional but recommended, multi-select)</Label>
          <div className="border rounded-md p-4 space-y-2 max-h-64 overflow-y-auto">
            {APPROVALS_NEEDED.map((approval) => (
              <div key={approval} className="flex items-center space-x-2">
                <Checkbox
                  id={`approval_${approval}`}
                  checked={profile.approvals_needed.includes(approval)}
                  onCheckedChange={() =>
                    toggleArrayItem(profile.approvals_needed, approval, "approvals_needed")
                  }
                />
                <Label
                  htmlFor={`approval_${approval}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {approval}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {profile.approvals_needed.includes("Other") && (
          <div>
            <Label htmlFor="other_approval">Please specify</Label>
            <Input
              id="other_approval"
              placeholder="Enter approval type"
              value={otherApproval}
              onChange={(e) => setOtherApproval(e.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderPage4 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Constraints & Sensitivities</h3>
        <p className="text-sm text-muted-foreground mb-4">
          This is what makes "How it impacts this project" credible and what drives specific next steps.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label>Constraint tags (optional but recommended, multi-select)</Label>
          <p className="text-xs text-muted-foreground mb-2">
            Keep to a curated set (users can pick 0–8)
          </p>
          <div className="border rounded-md p-4 space-y-2 max-h-64 overflow-y-auto">
            {CONSTRAINT_TAGS.map((constraint) => (
              <div key={constraint} className="flex items-center space-x-2">
                <Checkbox
                  id={`constraint_${constraint}`}
                  checked={profile.constraint_tags.includes(constraint)}
                  onCheckedChange={() =>
                    toggleArrayItem(profile.constraint_tags, constraint, "constraint_tags")
                  }
                />
                <Label
                  htmlFor={`constraint_${constraint}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {constraint}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {profile.constraint_tags.includes("Other") && (
          <div>
            <Label htmlFor="other_constraint">Please specify</Label>
            <Input
              id="other_constraint"
              placeholder="Enter constraint"
              value={otherConstraint}
              onChange={(e) => setOtherConstraint(e.target.value)}
            />
          </div>
        )}

        <div>
          <Label htmlFor="key_risk_note">Key risk note (optional)</Label>
          <Textarea
            id="key_risk_note"
            placeholder="Biggest risk to this project (1 sentence). (e.g., Servicing is the gating item; need clarity before submission.)"
            value={profile.key_risk_note || ""}
            onChange={(e) => updateProfile({ key_risk_note: e.target.value })}
            maxLength={200}
            rows={3}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {(profile.key_risk_note || "").length}/200 characters
          </p>
        </div>
      </div>
    </div>
  );

  const renderPage5 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Action & Notifications</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Align suggested next steps to how the team actually operates, and control where you receive project-relevant items.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="action_style">Action style *</Label>
          <p className="text-xs text-muted-foreground mb-2">
            This becomes the default "Suggested next step" framing for project-matched pulses
          </p>
          <Select
            value={profile.action_style}
            onValueChange={(value) => updateProfile({ action_style: value })}
          >
            <SelectTrigger id="action_style">
              <SelectValue placeholder="Select action style" />
            </SelectTrigger>
            <SelectContent>
              {ACTION_STYLES.map((style) => (
                <SelectItem key={style} value={style}>
                  {style}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="notifications">Notifications *</Label>
          <Select
            value={profile.notifications}
            onValueChange={(value) => updateProfile({ notifications: value })}
          >
            <SelectTrigger id="notifications">
              <SelectValue placeholder="Select notification preference" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="in-app">In-app only</SelectItem>
              <SelectItem value="in-app-email">In-app + email</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  const renderPage6 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Review & Save</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Confirm your profile at a glance and prevent accidental bad inputs.
        </p>
      </div>

      <div className="space-y-4 border rounded-md p-4">
        <div>
          <h4 className="font-semibold mb-2">Project Address</h4>
          <p className="text-sm">{profile.address}</p>
          {profile.municipality_id && (
            <p className="text-sm text-muted-foreground mt-1">
              Municipality: {municipalities.find(m => String(m.id) === profile.municipality_id)?.name || 'Unknown'}
            </p>
          )}
          {profile.nickname && <p className="text-sm text-muted-foreground mt-1">Nickname: {profile.nickname}</p>}
        </div>

        <div>
          <h4 className="font-semibold mb-2">Program</h4>
          <p className="text-sm">{profile.asset_type === "Other" && otherAssetType ? otherAssetType : profile.asset_type}</p>
          {profile.program_attributes.length > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              Attributes: {profile.program_attributes.filter(a => a !== "Other").join(", ")}
              {profile.program_attributes.includes("Other") && otherProgramAttribute && `, ${otherProgramAttribute}`}
            </p>
          )}
        </div>

        <div>
          <h4 className="font-semibold mb-2">Stage & Approvals</h4>
          <p className="text-sm">{profile.current_stage}</p>
          {profile.approvals_needed.length > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              Approvals: {profile.approvals_needed.filter(a => a !== "Other").join(", ")}
              {profile.approvals_needed.includes("Other") && otherApproval && `, ${otherApproval}`}
            </p>
          )}
        </div>

        <div>
          <h4 className="font-semibold mb-2">Constraints</h4>
          {profile.constraint_tags.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              {profile.constraint_tags.filter(c => c !== "Other").join(", ")}
              {profile.constraint_tags.includes("Other") && otherConstraint && `, ${otherConstraint}`}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">None selected</p>
          )}
          {profile.key_risk_note && (
            <p className="text-sm text-muted-foreground mt-2">Key risk: {profile.key_risk_note}</p>
          )}
        </div>

        <div>
          <h4 className="font-semibold mb-2">Action & Notifications</h4>
          <p className="text-sm">{profile.action_style}</p>
          <p className="text-sm text-muted-foreground mt-1">
            Notifications: {profile.notifications === "in-app" ? "In-app only" : "In-app + email"}
          </p>
        </div>
      </div>

      <div className="text-sm text-muted-foreground">
        <p>You can edit these settings later from the Projects page.</p>
      </div>
    </div>
  );

  const renderPageContent = () => {
    switch (currentPage) {
      case 1:
        return renderPage1();
      case 2:
        return renderPage2();
      case 3:
        return renderPage3();
      case 4:
        return renderPage4();
      case 5:
        return renderPage5();
      case 6:
        return renderPage6();
      default:
        return null;
    }
  };

  const pageTitles = [
    "Project Address",
    "Program",
    "Stage & Approvals",
    "Constraints & Sensitivities",
    "Action & Notifications",
    "Review & Save",
  ];

  const handleDialogClose = (isOpen: boolean) => {
    // Only allow closing if on first page or if onClose is provided
    if (!isOpen && currentPage === 1 && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto [&>button]:hidden" 
        onInteractOutside={(e) => {
          // Allow closing on first page, prevent on others
          if (currentPage === 1 && onClose) {
            onClose();
          } else {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Allow closing on first page, prevent on others
          if (currentPage === 1 && onClose) {
            onClose();
          } else {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{projectId ? "Edit Project" : "Set Up Project Profile"}</DialogTitle>
          <DialogDescription>
            {projectId 
              ? `Step ${currentPage} of 6: ${pageTitles[currentPage - 1]}`
              : `Step ${currentPage} of 6: ${pageTitles[currentPage - 1]}`}
          </DialogDescription>
          <p className="text-xs text-muted-foreground mt-2">
            Complete this setup to start receiving tailored pulses for your project.
          </p>
        </DialogHeader>

        <div className="mt-4">
          {/* Progress indicator */}
          <div className="flex items-center justify-between mb-6">
            {[1, 2, 3, 4, 5, 6].map((page) => (
              <React.Fragment key={page}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    page === currentPage
                      ? "bg-primary text-primary-foreground"
                      : page < currentPage
                      ? "bg-primary/20 text-primary"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {page}
                </div>
                {page < 6 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      page < currentPage ? "bg-primary" : "bg-muted"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {renderPageContent()}
        </div>

        <div className="flex justify-between mt-6 pt-4 border-t">
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleBack}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              {currentPage === 1 ? "Cancel" : "Back"}
            </Button>
            {currentPage > 1 && (
              <Button
                variant="outline"
                onClick={handleSaveProgress}
                disabled={isSavingProgress || isSubmitting}
              >
                <Save className="w-4 h-4 mr-2" />
                {isSavingProgress ? "Saving..." : "Save Progress"}
              </Button>
            )}
          </div>
          {currentPage < 6 ? (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSave} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : projectId ? "Update Project" : "Save Project Profile"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

